import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import Button from '@/components/ui/button';
import PaymentScreen from '@/components/PaymentScreen';
import { GiftSummaryChip } from '@/components/LandingContext';
import { useOpenBankingPaymentStatus } from '@/tsq/queries';
import { useOpenBankingRestartMutation } from '@/tsq/mutations';
import { redirectToExternal } from '@/utils/helpers/navigation.helpers';
import { formatCurrency } from '@/utils/formatters/currency';
import { miaConsoleLog } from '@/utils/mia-logger';
import { track } from '@/analytics';
import {
  useAmount,
  useChildFirstName,
  useContributionId,
  useGiftImageUrl,
  useGiftInviteCode,
  useHydrated,
  useOpenBankingId,
  useShowChildPhoto,
} from '@/store';

import {
  openBankingStatuses,
  PAYMENT_ABANDONED_GRACE_MS,
  TRUELAYER_ABANDONED_ERROR,
} from '@/constants';

/**
 * Where the bank returns the contributor (GIFTWEB-26 AC 3, AC 4, AC 9). The
 * screen polls our own record of the payment — TrueLayer webhooks into it — and
 * shows a processing state until it settles.
 *
 * Everything that used to leave for `/error` or raise a toast now renders here
 * instead: the V2 design has no toasts, and dropping someone on a generic error
 * page mid-payment tells them nothing about their money.
 */
function PaymentCallback() {
  const isHydrated = useHydrated();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const contributionId = useContributionId();
  const giftInviteCode = useGiftInviteCode();
  const storedPaymentId = useOpenBankingId();
  const amount = useAmount();
  const childFirstName = useChildFirstName();
  const photoSignedUrl = useGiftImageUrl();
  const showChildPhoto = useShowChildPhoto();

  const [pollingEnabled, setPollingEnabled] = useState(true);

  // TrueLayer tells us on the way back when the contributor walked away from
  // its hosted page. Our API is still the source of truth, but this stops them
  // watching a spinner for the full window when we already know how it ended.
  const abandoned = searchParams.get('error') === TRUELAYER_ABANDONED_ERROR;

  // TrueLayer returns the id in the query string; the stored one covers a
  // return that lost it, and a re-entry we routed here ourselves.
  const paymentId = searchParams.get('payment_id') || storedPaymentId || '';

  const hasContext = !!paymentId && !!contributionId && !!giftInviteCode;

  const {
    data: statusResponse,
    isError,
    error,
    hasTimedOut,
    refetch,
  } = useOpenBankingPaymentStatus({
    contributionId,
    code: giftInviteCode,
    paymentId,
    enabled: isHydrated && hasContext && pollingEnabled,
  });

  const { mutateAsync: mutateOpenBankingRestart } =
    useOpenBankingRestartMutation();

  const settledStatus = statusResponse?.data?.status;

  // Give a webhook a short chance to land before believing TrueLayer.
  useEffect(() => {
    if (!abandoned || !isHydrated || !hasContext) return;
    if (settledStatus && settledStatus !== openBankingStatuses.created && settledStatus !== openBankingStatuses.pending) {
      return;
    }

    const timer = setTimeout(() => {
      miaConsoleLog('info', {
        event: 'payment_callback_abandoned_shortcut',
        paymentId,
        contributionId,
      });
      setPollingEnabled(false);
      track('paymentNotCompleted', { outcome: openBankingStatuses.cancelled });
      navigate(`/payment-failed?status=${openBankingStatuses.cancelled}`, {
        replace: true,
      });
    }, PAYMENT_ABANDONED_GRACE_MS);

    return () => clearTimeout(timer);
  }, [
    abandoned,
    isHydrated,
    hasContext,
    settledStatus,
    paymentId,
    contributionId,
    navigate,
  ]);

  useEffect(() => {
    if (!isHydrated || !hasContext) return;

    (async () => {
      if (hasTimedOut) {
        miaConsoleLog('warn', {
          event: 'payment_callback_polling_timed_out',
          paymentId,
          contributionId,
        });
        setPollingEnabled(false);
        // AC 8 — still pending after the window is not a failure.
        track('paymentNotCompleted', { outcome: openBankingStatuses.pending });
        await navigate(`/payment-failed?status=${openBankingStatuses.pending}`, {
          replace: true,
        });
        return;
      }

      const status = statusResponse?.data?.status;
      if (!status) return;

      if (
        status === openBankingStatuses.created ||
        status === openBankingStatuses.pending
      ) {
        return;
      }

      if (status === openBankingStatuses.succeeded) {
        setPollingEnabled(false);
        track('paymentSucceeded');
        // Replaced, so Back cannot return to the callback (AC 4).
        await navigate(`/easybanking/success?payment_id=${paymentId}`, {
          replace: true,
        });
        return;
      }

      miaConsoleLog('error', {
        event: 'payment_callback_terminal_status',
        paymentId,
        contributionId,
        status,
      });

      setPollingEnabled(false);

      // AC 7 — expiry is nobody's decision, so it restarts without asking.
      // Everything else goes to the outcome screen, which owns its own retry.
      if (status === openBankingStatuses.expired) {
        try {
          const restartResponse = await mutateOpenBankingRestart({
            uuid: contributionId,
            code: giftInviteCode,
          });
          const restartUrl: string | undefined =
            restartResponse?.data?.truelayer?.authoriseUrl;

          if (restartUrl) {
            // Back to the bank rather than to the failure screen, so this is
            // another payment attempt, not an abandoned one.
            track('paymentStarted');
            redirectToExternal(restartUrl);
            return;
          }
        } catch (restartError) {
          miaConsoleLog('error', {
            event: 'payment_callback_restart_mutation_error',
            paymentId,
            contributionId,
            status,
            errorMessage:
              restartError instanceof Error
                ? restartError.message
                : String(restartError),
          });
        }
      }

      track('paymentNotCompleted', { outcome: status });
      await navigate(`/payment-failed?status=${status}`, { replace: true });
    })();
    // Keyed on the payment's identity and the polling outcome; re-running when
    // the mutation or navigate identity changes would re-enter an in-flight
    // redirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated, hasContext, paymentId, contributionId, statusResponse, hasTimedOut]);

  // ── Guards ────────────────────────────────────────────────────────────────

  if (!isHydrated) return null;

  const amountLabel = formatCurrency({ amount, precision: 0 });
  const child = childFirstName?.trim();

  const summary = (
    <GiftSummaryChip
      firstName={childFirstName}
      photoUrl={photoSignedUrl}
      showPhoto={showChildPhoto}
      amountLabel={amountLabel}
    />
  );

  if (!giftInviteCode) {
    return (
      <PaymentScreen
        className='payment-callback-page'
        mood='asking'
        heading="We've lost track of your gift"
        body='Please open the gift link you were sent, and we will pick up from there.'
      />
    );
  }

  if (!contributionId) {
    return (
      <PaymentScreen
        className='payment-callback-page'
        mood='asking'
        heading="We've lost track of your gift"
        body='We could not match this payment to your gift. Open your gift link again to start over.'
        action={
          <Button asChild className='flex w-full'>
            <Link to={`/?code=${giftInviteCode}`}>Start again</Link>
          </Button>
        }
      />
    );
  }

  // AC 9 — nothing identifies the payment, so send them back to the step that
  // creates one rather than leaving them guessing.
  if (!paymentId) {
    return (
      <PaymentScreen
        className='payment-callback-page'
        summary={summary}
        mood='asking'
        heading="We couldn't find your payment"
        body='Your bank did not tell us which payment this was, so we cannot check it. Nothing has been taken — you can start the payment again.'
        action={
          <Button asChild className='flex w-full'>
            <Link to='/payment'>Back to payment</Link>
          </Button>
        }
      />
    );
  }

  if (isError) {
    console.error('Error fetching payment status:', error);
    return (
      <PaymentScreen
        className='payment-callback-page'
        summary={summary}
        mood='asking'
        heading="We couldn't check your payment"
        body='We lost our connection while checking how your payment went. Your bank may still have processed it, so please check with them before paying again.'
        action={
          <Button className='block w-full' onClick={() => refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  return (
    <PaymentScreen
      className='payment-callback-page'
      // AC 2 — returning from the bank; the outcome is seconds away.
      showHelp={false}
      summary={summary}
      mood='thinking'
      heading="We're confirming your payment…"
      body={
        child
          ? `This usually takes a few seconds. Please keep this window open while we check with your bank about your gift to ${child}.`
          : 'This usually takes a few seconds. Please keep this window open while we check with your bank.'
      }
      busy
    />
  );
}

export default PaymentCallback;
